// services/socketService.js
class SocketService {
  constructor() {
    this.ws = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.eventCallbacks = new Map();
    this.connectionPromise = null;
    this.pingInterval = null;
    this.messageQueue = [];
    this.competitionId = null;

    // AWS API Gateway WebSocket URL from environment
    this.wsUrl = process.env.REACT_APP_WS_URL || 'wss://pp7jq4cewc.execute-api.ap-south-1.amazonaws.com/production';

    // Message types mapping
    this.messageTypes = {
      'timer-update': 'timer-update',
      'timer-error': 'timer-error',
      'timer-action-confirmed': 'timer-action-confirmed',
      'user-joined': 'user-joined',
      'user-left': 'user-left',
      'room-info': 'room-info',
      'connected': 'connected',
      'error': 'error'
    };

    // Bind methods to maintain context
    this.handleWebSocketOpen = this.handleWebSocketOpen.bind(this);
    this.handleWebSocketMessage = this.handleWebSocketMessage.bind(this);
    this.handleWebSocketClose = this.handleWebSocketClose.bind(this);
    this.handleWebSocketError = this.handleWebSocketError.bind(this);
    this.attemptReconnection = this.attemptReconnection.bind(this);
  }

  async connect() {
    // If already connected, return the existing connection
    if (this.ws?.readyState === WebSocket.OPEN) {
      return Promise.resolve(this.ws);
    }

    // If connecting in progress, return the existing promise
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      try {
        console.log('🔄 Connecting to AWS WebSocket...', this.wsUrl);

        // Close existing connection gracefully
        if (this.ws) {
          try {
            this.ws.close(1000, 'Reconnecting');
          } catch (e) {
            // Silent fail
          }
          this.ws = null;
        }

        // Create new WebSocket connection
        this.ws = new WebSocket(this.wsUrl);

        // Set up event handlers
        this.ws.onopen = this.handleWebSocketOpen(resolve);
        this.ws.onmessage = this.handleWebSocketMessage;
        this.ws.onclose = this.handleWebSocketClose;
        this.ws.onerror = this.handleWebSocketError(reject);

        // Set connection timeout
        setTimeout(() => {
          if (this.ws?.readyState === WebSocket.CONNECTING) {
            console.warn('⚠️ WebSocket connection timeout');
            try {
              this.ws.close();
            } catch (e) {
              // Silent fail
            }
            this.connectionPromise = null;

            // Don't reject with error, just resolve with null to avoid console errors
            resolve(null);
          }
        }, 10000);

      } catch (error) {
        console.warn('⚠️ Failed to initialize WebSocket:', error.message);
        this.connectionPromise = null;

        // Don't reject, resolve with null to prevent uncaught promise errors
        resolve(null);
      }
    });

    return this.connectionPromise;
  }

  handleWebSocketOpen(resolve) {
    return () => {
      console.log('✅ WebSocket connected to AWS');
      clearTimeout(this.connectionTimeout);
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.connectionPromise = null;

      // Setup ping interval
      this.setupPing();

      // Process queued messages
      this.processMessageQueue();

      resolve(this.ws);
    };
  }

  handleWebSocketMessage(event) {
    try {
      const data = JSON.parse(event.data);
      this.handleIncomingMessage(data);
    } catch (error) {
      console.warn('⚠️ Error parsing WebSocket message:', error.message);
    }
  }

  handleWebSocketClose(event) {
    console.log('🔌 WebSocket disconnected:', {
      code: event.code,
      reason: event.reason,
      wasClean: event.wasClean
    });

    this.isConnected = false;
    this.connectionPromise = null;
    this.clearPing();

    // Only attempt reconnect if it wasn't a clean close (1000) or normal closure
    if (event.code !== 1000 && event.code !== 1001) {
      this.attemptReconnection();
    }
  }

  handleWebSocketError(reject) {
    return (error) => {
      console.warn('⚠️ WebSocket error occurred');
      this.isConnected = false;
      this.connectionPromise = null;
      this.clearPing();

      // Don't reject, this will prevent uncaught promise errors
      // Instead, we'll handle reconnection in onclose
    };
  }

  attemptReconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

      console.log(`🔄 Attempting reconnect in ${delay}ms (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

      setTimeout(() => {
        if (!this.isConnected) {
          this.connect().catch(() => {
            // Silently handle connection errors
          });
        }
      }, delay);
    } else {
      console.warn('⚠️ Max reconnection attempts reached');
    }
  }

  handleIncomingMessage(data) {
    try {
      const { type, action, ...rest } = data;
      console.log('📨 Received WebSocket message:', { type, action });

      // Map AWS WebSocket message types to frontend events
      switch (type) {
        case 'timer-update':
          this.emitEvent('timer-update', rest.data || rest);
          break;

        case 'timer-error':
        case 'error':
          this.emitEvent('timer-error', {
            message: rest.message || 'Unknown error',
            ...rest
          });
          break;

        case 'timer-action-confirmed':
          console.log('✅ Action confirmed:', action);
          break;

        case 'user-joined':
        case 'user-left':
        case 'room-info':
          // Handle other events if needed
          break;

        case 'connected':
          console.log('✅ Connection established:', rest.message);
          break;

        default:
          console.log('📨 Received unknown message type:', type, rest);
      }
    } catch (error) {
      console.warn('⚠️ Error handling incoming message:', error.message);
    }
  }

  setupPing() {
    // Clear existing interval
    this.clearPing();

    // Send ping every 30 seconds to keep connection alive
    this.pingInterval = setInterval(() => {
      if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.sendMessage({ action: 'ping' });
        } catch (error) {
          console.warn('⚠️ Error sending ping:', error.message);
        }
      }
    }, 30000);
  }

  clearPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

    sendMessage(message) {
    if (!this.isConnected || this.ws?.readyState !== WebSocket.OPEN) {
      console.log('📦 Queueing message (disconnected):', message);
      this.messageQueue.push(message);
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      this.ws.send(messageStr);
      return true;
    } catch (error) {
      console.warn('⚠️ Error sending message:', error.message);
      this.messageQueue.push(message);
      return false;
    }
  }

  processMessageQueue() {
    try {
      while (this.messageQueue.length > 0 && this.isConnected) {
        const message = this.messageQueue.shift();
        this.sendMessage(message);
      }
    } catch (error) {
      console.warn('⚠️ Error processing message queue:', error.message);
    }
  }

  // Competition management
  joinCompetition(competitionId, userInfo = {}) {
    try {
      this.competitionId = competitionId;

      const joinMessage = {
        action: 'joinCompetition',
        competitionId,
        userId: userInfo.userId || 'admin',
        userType: userInfo.userType || 'admin'
      };

      return this.sendMessage(joinMessage);
    } catch (error) {
      console.warn('⚠️ Error joining competition:', error.message);
      return false;
    }
  }

  leaveCompetition(competitionId) {
    try {
      const leaveMessage = {
        action: 'leaveCompetition',
        competitionId
      };

      this.sendMessage(leaveMessage);

      if (this.competitionId === competitionId) {
        this.competitionId = null;
      }
    } catch (error) {
      console.warn('⚠️ Error leaving competition:', error.message);
    }
  }

  // Timer control methods - updated for AWS WebSocket
  startCountdown(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'startCountdown',
      competitionId
    });
  }

  startClimbing(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'startClimbing',
      competitionId
    });
  }

  startWrapTime(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'startWrapTime',
      competitionId
    });
  }

  pauseTimer(competitionId, reason = "Manual pause", pausedBy = null) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'pause',
      competitionId,
      reason,
      pausedBy
    });
  }

  resumeTimer(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'resume',
      competitionId
    });
  }

  resetTimer(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'reset',
      competitionId
    });
  }

  completeRound(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'completeRound',
      competitionId
    });
  }

  skipRound(competitionId) {
    return this.sendMessage({
      action: 'timerControl',
      command: 'skipRound',
      competitionId
    });
  }

  async getTimerState(competitionId) {
    return this.sendMessage({
      action: 'getTimerState',
      competitionId
    });
  }

  getStatistics(competitionId) {
    return this.sendMessage({
      action: 'getStatistics',
      competitionId
    });
  }

  refreshTimer(competitionId) {
    // For AWS WebSocket, we just request timer state
    return this.getTimerState(competitionId);
  }

  // Event handling
  onTimerUpdate(callback) {
    this.addEventListener('timer-update', callback);
  }

  onTimerError(callback) {
    this.addEventListener('timer-error', callback);
  }

  addEventListener(event, callback) {
    try {
      if (!this.eventCallbacks.has(event)) {
        this.eventCallbacks.set(event, []);
      }
      this.eventCallbacks.get(event).push(callback);
    } catch (error) {
      console.warn(`⚠️ Error adding event listener for ${event}:`, error.message);
    }
  }

  removeEventListener(event, callback) {
    try {
      if (this.eventCallbacks.has(event)) {
        const callbacks = this.eventCallbacks.get(event);
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    } catch (error) {
      console.warn(`⚠️ Error removing event listener for ${event}:`, error.message);
    }
  }

  emitEvent(event, data) {
    try {
      const callbacks = this.eventCallbacks.get(event) || [];
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (callbackError) {
          console.warn(`⚠️ Error in ${event} callback:`, callbackError.message);
        }
      });
    } catch (error) {
      console.warn(`⚠️ Error emitting event ${event}:`, error.message);
    }
  }

  // Cleanup
  offTimerUpdate() {
    try {
      this.eventCallbacks.delete('timer-update');
    } catch (error) {
      console.warn('⚠️ Error removing timer update listeners:', error.message);
    }
  }

  offTimerError() {
    try {
      this.eventCallbacks.delete('timer-error');
    } catch (error) {
      console.warn('⚠️ Error removing timer error listeners:', error.message);
    }
  }

  disconnect() {
    try {
      this.clearPing();

      if (this.ws) {
        try {
          this.ws.close(1000, 'Client disconnected');
        } catch (e) {
          // Silent fail
        }
        this.ws = null;
      }

      this.isConnected = false;
      this.eventCallbacks.clear();
      this.messageQueue = [];
      this.competitionId = null;
      this.connectionPromise = null;
      console.log('🔌 WebSocket disconnected gracefully');
    } catch (error) {
      console.warn('⚠️ Error during disconnect:', error.message);
    }
  }

  getConnectionStatus() {
    return this.isConnected && this.ws?.readyState === WebSocket.OPEN;
  }

  // Add a safe connect method that never throws
  safeConnect() {
    return this.connect().catch(error => {
      console.warn('⚠️ Safe connect caught error:', error.message);
      return null;
    });
  }
}

export default new SocketService();